/**
 * Who is using the page, as far as the page itself is concerned.
 *
 * Signing in is Clerk's; deciding who may drive is the backend's (it checks every call against
 * the portal's list). So "signed in" here only means Clerk has a session, and the page still
 * asks the backend who that is before it goes anywhere near the relay.
 */
import { ClerkLoaded, ClerkLoading, ClerkProvider, Show, SignIn, getToken, useClerk } from "@clerk/react";
import React, { createContext, useContext, useEffect, useState } from "react";

import { fetchMe, HttpError, sendSessionTokenFrom, type Me } from "../api/client";

type Account = { signOut: (() => void) | null };

const AccountContext = createContext<Account>({ signOut: null });

export const useAccount = (): Account => useContext(AccountContext);

// The page's own colours, so the sign-in box does not arrive as a white card on a dark page.
const APPEARANCE = {
  variables: {
    colorPrimary: "#5b9dff",
    colorBackground: "#14181d",
    colorForeground: "#e8ecf1",
    colorMutedForeground: "#8c98a6",
    colorNeutral: "#e8ecf1",
    colorInput: "#0b0d10",
    colorInputForeground: "#e8ecf1",
    borderRadius: "10px",
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  },
};

export function ClerkGate({ publishableKey, children }: { publishableKey: string; children: React.ReactNode }) {
  return (
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/" appearance={APPEARANCE}>
      <ClerkLoading>
        <Frame><p className="empty">loading sign-in…</p></Frame>
      </ClerkLoading>
      <ClerkLoaded>
        <Show when="signed-out">
          <Frame>
            <div className="sign-in">
              {/* Hash routing: the sign-in's own steps live after the #, so they never meet
                  the page's router and a deep link like /robots/yam survives signing in. */}
              <SignIn routing="hash" />
            </div>
          </Frame>
        </Show>
        <Show when="signed-in">
          <SignedIn>{children}</SignedIn>
        </Show>
      </ClerkLoaded>
    </ClerkProvider>
  );
}

function SignedIn({ children }: { children: React.ReactNode }) {
  const clerk = useClerk();
  // Set while rendering, not in an effect: children's effects run before their parent's, so
  // their first request would otherwise go out bare. Never unset -- once signed out, Clerk's
  // getToken answers null by itself.
  sendSessionTokenFrom(getToken);
  const account: Account = { signOut: () => void clerk.signOut({ redirectUrl: "/" }) };
  return <AccountContext.Provider value={account}>{children}</AccountContext.Provider>;
}

/** Ask the backend who this is, and render the portal only for someone on its list. */
export function WhoAreYou({ children }: { children: (me: Me) => React.ReactNode }) {
  const { signOut } = useAccount();
  const [me, setMe] = useState<Me | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((it) => { if (!cancelled) { setMe(it); setTrouble(null); } })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 403) return setRefused(err.message);
        // Anything else is the portal's trouble or the network's, not a verdict: try again.
        setTrouble(err instanceof Error ? err.message : String(err));
        window.setTimeout(() => !cancelled && setAttempt((n) => n + 1), 3000);
      });
    return () => { cancelled = true; };
  }, [attempt]);

  if (me) return <>{children(me)}</>;
  if (refused) {
    return (
      <Frame>
        <div className="refused">
          <h1>Not on the list</h1>
          <p>{refused}.</p>
          <p className="dim">Sign in with an address this portal allows, or ask whoever runs it to add yours.</p>
          {signOut && <button className="primary" onClick={signOut}>Sign out and try another account</button>}
        </div>
      </Frame>
    );
  }
  return <Frame><p className="empty">{trouble ? `cannot check who you are: ${trouble}. Trying again…` : "signing in…"}</p></Frame>;
}

/** The page's header around something that is not the portal yet. */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <>
      <header className="top">
        <a className="mark" href="/">operator</a>
      </header>
      <main>{children}</main>
    </>
  );
}
